import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Collapse,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import FilterListIcon from '@mui/icons-material/FilterList'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { useDebounce } from 'src/customHooks/useDebounce'

export default function ModernizedFilters({
  filters,
  onFilterChange,
  onClearFilters,
  sort,
  onSortChange,
}) {
  const [showFilters, setShowFilters] = useState(false)
  const [localDateFrom, setLocalDateFrom] = useState(null)
  const [localDateTo, setLocalDateTo] = useState(null)
  const [doctors, setDoctors] = useState([])
  const [loadingDoctors, setLoadingDoctors] = useState(false)
  const [clinicBrands, setClinicBrands] = useState([])
  const [clinicLocations, setClinicLocations] = useState([])
  const [loadingClinics, setLoadingClinics] = useState(false)
  const [patientSearch, setPatientSearch] = useState(filters.search || '')
  const debouncedPatientSearch = useDebounce(patientSearch, 500)

  // Sync patient search from filters (when cleared externally)
  useEffect(() => {
    if (filters.search !== patientSearch && filters.search !== debouncedPatientSearch) {
      setPatientSearch(filters.search || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search])

  // Sync local date state with filters
  useEffect(() => {
    if (filters.from) {
      const date = typeof filters.from === 'string' ? dayjs(filters.from) : dayjs(filters.from)
      setLocalDateFrom(date.isValid() ? date : null)
    } else {
      setLocalDateFrom(null)
    }
  }, [filters.from])

  useEffect(() => {
    if (filters.to) {
      const date = typeof filters.to === 'string' ? dayjs(filters.to) : dayjs(filters.to)
      setLocalDateTo(date.isValid() ? date : null)
    } else {
      setLocalDateTo(null)
    }
  }, [filters.to])

  // Sync patient search with filter (only when debounced value changes)
  useEffect(() => {
    if (debouncedPatientSearch !== filters.search) {
      onFilterChange('search', debouncedPatientSearch)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPatientSearch])

  // Fetch clinic brands and locations
  const fetchClinics = useCallback(async () => {
    setLoadingClinics(true)
    try {
      const [brandsResp, locationsResp] = await Promise.all([
        getSPDataWithParams('/api/secure/cc/support/clinic-brand/list', {}),
        getSPDataWithParams('/api/secure/cc/support/clinic/list', {}),
      ])

      if (brandsResp.status === 200 && isCommonResponseSuccessfulV2(brandsResp)) {
        const brandsData = getDataMapFromCommonResponse(brandsResp)?.['clinic-brand'] || []
        setClinicBrands(brandsData)
      }

      if (locationsResp.status === 200 && isCommonResponseSuccessfulV2(locationsResp)) {
        const locationsData = getDataMapFromCommonResponse(locationsResp)?.['clinic'] || []
        setClinicLocations(locationsData)
      }
    } catch (error) {
      console.error('Error fetching clinics:', error)
    } finally {
      setLoadingClinics(false)
    }
  }, [])

  // Fetch doctors list
  const fetchDoctors = useCallback(async () => {
    setLoadingDoctors(true)
    try {
      const response = await getSPDataWithParams('/api/secure/cc/support/doctor/list', {})
      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const doctorsList = getDataMapFromCommonResponse(response)?.mentor || []
        setDoctors(doctorsList)
      }
    } catch (error) {
      console.error('Error fetching doctors:', error)
      setDoctors([])
    } finally {
      setLoadingDoctors(false)
    }
  }, [])

  useEffect(() => {
    fetchClinics()
    fetchDoctors()
  }, [fetchClinics, fetchDoctors])

  // Filter locations by selected brand
  const filteredLocations = useMemo(() => {
    if (!filters.clinicBrandId) return clinicLocations
    return clinicLocations.filter((loc) => loc?.clinicBrandId === filters.clinicBrandId)
  }, [clinicLocations, filters.clinicBrandId])

  // Get selected doctor
  const selectedDoctor = useMemo(() => {
    if (!filters.triggeringDoctorId && !filters.mentorId) return null
    return doctors.find(
      (doc) => doc.id === filters.triggeringDoctorId || doc.id === filters.mentorId,
    )
  }, [doctors, filters.triggeringDoctorId, filters.mentorId])

  const handleDateFromChange = (date) => {
    setLocalDateFrom(date)
    onFilterChange('from', date ? date.toISOString() : '')
  }

  const handleDateToChange = (date) => {
    setLocalDateTo(date)
    onFilterChange('to', date ? date.toISOString() : '')
  }

  const handleDoctorChange = (event, newValue) => {
    if (newValue) {
      onFilterChange('triggeringDoctorId', newValue.id)
      onFilterChange('mentorId', newValue.id)
      onFilterChange('triggeringDoctorSlug', newValue.slug || '')
    } else {
      onFilterChange('triggeringDoctorId', '')
      onFilterChange('mentorId', '')
      onFilterChange('triggeringDoctorSlug', '')
    }
  }

  const handleArrayFilterChange = (key, value) => {
    let arrayValue = []
    if (typeof value === 'string' && value.trim()) {
      arrayValue = value.split(',').map((item) => item.trim()).filter(Boolean)
    } else if (Array.isArray(value)) {
      arrayValue = value
    }
    onFilterChange(key, arrayValue.length > 0 ? arrayValue : '')
  }

  // Get active filters for tags
  const activeFilters = useMemo(() => {
    const active = []
    
    if (filters.search) {
      active.push({ key: 'search', label: `Search: ${filters.search}`, value: filters.search })
    }
    if (filters.from) {
      active.push({ key: 'from', label: `From: ${dayjs(filters.from).format('DD MMM YYYY')}`, value: filters.from })
    }
    if (filters.to) {
      active.push({ key: 'to', label: `To: ${dayjs(filters.to).format('DD MMM YYYY')}`, value: filters.to })
    }
    if (filters.triggeringDoctorId) {
      const doctor = doctors.find((d) => d.id === filters.triggeringDoctorId)
      active.push({ key: 'triggeringDoctorId', label: `Doctor: ${doctor?.name || 'Selected'}`, value: filters.triggeringDoctorId })
    }
    if (filters.clinicBrandId) {
      const brand = clinicBrands.find((b) => b.id === filters.clinicBrandId)
      active.push({ key: 'clinicBrandId', label: `Brand: ${brand?.clinicBrandName || brand?.name || 'Selected'}`, value: filters.clinicBrandId })
    }
    if (filters.clinicLocationId) {
      const location = clinicLocations.find((l) => l.id === filters.clinicLocationId)
      active.push({ key: 'clinicLocationId', label: `Location: ${location?.clinicName || location?.name || 'Selected'}`, value: filters.clinicLocationId })
    }
    if (filters.interventionType) {
      active.push({ key: 'interventionType', label: `Type: ${filters.interventionType}`, value: filters.interventionType })
    }
    if (filters.prescribedTherapy?.length > 0) {
      active.push({ key: 'prescribedTherapy', label: `Therapy: ${Array.isArray(filters.prescribedTherapy) ? filters.prescribedTherapy.join(', ') : filters.prescribedTherapy}`, value: filters.prescribedTherapy })
    }
    if (filters.prescribedAssessments?.length > 0) {
      active.push({ key: 'prescribedAssessments', label: `Assessment: ${Array.isArray(filters.prescribedAssessments) ? filters.prescribedAssessments.join(', ') : filters.prescribedAssessments}`, value: filters.prescribedAssessments })
    }
    if (filters.suggestedBookingTherapy !== null && filters.suggestedBookingTherapy !== undefined) {
      active.push({ key: 'suggestedBookingTherapy', label: `Suggested Booking: ${filters.suggestedBookingTherapy ? 'Yes' : 'No'}`, value: filters.suggestedBookingTherapy })
    }

    return active
  }, [filters, doctors, clinicBrands, clinicLocations])

  const removeFilter = (key) => {
    if (key === 'from') {
      setLocalDateFrom(null)
      onFilterChange('from', '')
    } else if (key === 'to') {
      setLocalDateTo(null)
      onFilterChange('to', '')
    } else if (key === 'search') {
      setPatientSearch('')
      onFilterChange('search', '')
    } else if (key === 'triggeringDoctorId') {
      onFilterChange('triggeringDoctorId', '')
      onFilterChange('mentorId', '')
      onFilterChange('triggeringDoctorSlug', '')
    } else if (key === 'clinicBrandId') {
      onFilterChange('clinicBrandId', '')
      onFilterChange('clinicLocationId', '') // Clear location when brand is cleared
    } else {
      onFilterChange(key, '')
    }
  }

  // Parse sort string (e.g., "startTime,desc")
  const sortField = sort?.split(',')[0] || 'startTime'
  const sortDirection = sort?.split(',')[1] || 'desc'

  const handleSortChange = (field) => {
    if (sortField === field) {
      // Toggle direction
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
      onSortChange(`${field},${newDirection}`)
    } else {
      // New field, default to desc
      onSortChange(`${field},desc`)
    }
  }

  return (
    <Box>
      {/* Always Visible Search Box */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <TextField
          fullWidth
          size="medium"
          placeholder="Search by patient name, patient number, mobile, or email..."
          value={patientSearch}
          onChange={(e) => setPatientSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: patientSearch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setPatientSearch('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
      </Paper>

      {/* Applied Filters Tags - Frozen on Scroll */}
      {activeFilters.length > 0 && (
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ alignSelf: 'center', fontWeight: 600, mr: 0.5 }}>
              Active Filters:
            </Typography>
            {activeFilters.map((filter) => (
              <Chip
                key={filter.key}
                label={filter.label}
                onDelete={() => removeFilter(filter.key)}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            <Chip
              label="Clear All"
              onClick={onClearFilters}
              size="small"
              color="error"
              variant="outlined"
              sx={{ ml: 'auto' }}
            />
          </Stack>
        </Paper>
      )}

      {/* Filters Section */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              sx={{ color: 'primary.main' }}
            >
              {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography variant="subtitle2" fontWeight={600}>
              Filters
            </Typography>
          </Box>
        </Box>

        <Collapse in={showFilters}>
          <Paper sx={{ p: 3, borderRadius: 2 }} elevation={1}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              {/* Date Range */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={localDateFrom}
                  onChange={handleDateFromChange}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={localDateTo}
                  onChange={handleDateToChange}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>

              {/* Doctor */}
              <Autocomplete
                size="small"
                options={doctors}
                value={selectedDoctor}
                onChange={handleDoctorChange}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                loading={loadingDoctors}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Triggering Doctor"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingDoctors ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Clinic Brand */}
              <FormControl fullWidth size="small">
                <InputLabel>Clinic Brand</InputLabel>
                <Select
                  value={filters.clinicBrandId || ''}
                  onChange={(e) => {
                    onFilterChange('clinicBrandId', e.target.value)
                    onFilterChange('clinicLocationId', '') // Clear location when brand changes
                  }}
                  label="Clinic Brand"
                  disabled={loadingClinics}
                >
                  <MenuItem value="">All</MenuItem>
                  {clinicBrands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.clinicBrandName || brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Clinic Location */}
              <FormControl fullWidth size="small">
                <InputLabel>Clinic Location</InputLabel>
                <Select
                  value={filters.clinicLocationId || ''}
                  onChange={(e) => onFilterChange('clinicLocationId', e.target.value)}
                  label="Clinic Location"
                  disabled={loadingClinics || !filters.clinicBrandId}
                >
                  <MenuItem value="">All</MenuItem>
                  {filteredLocations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.clinicName || location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Intervention Type */}
              <FormControl fullWidth size="small">
                <InputLabel>Intervention Type</InputLabel>
                <Select
                  value={filters.interventionType || ''}
                  onChange={(e) => onFilterChange('interventionType', e.target.value)}
                  label="Intervention Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="therapy">Therapy</MenuItem>
                  <MenuItem value="assessment">Assessment</MenuItem>
                </Select>
              </FormControl>

              {/* Prescribed Therapy */}
              <TextField
                fullWidth
                size="small"
                label="Prescribed Therapy"
                value={Array.isArray(filters.prescribedTherapy) ? filters.prescribedTherapy.join(', ') : filters.prescribedTherapy || ''}
                onChange={(e) => handleArrayFilterChange('prescribedTherapy', e.target.value)}
                placeholder="Comma-separated values"
              />

              {/* Prescribed Assessments */}
              <TextField
                fullWidth
                size="small"
                label="Prescribed Assessments"
                value={Array.isArray(filters.prescribedAssessments) ? filters.prescribedAssessments.join(', ') : filters.prescribedAssessments || ''}
                onChange={(e) => handleArrayFilterChange('prescribedAssessments', e.target.value)}
                placeholder="Comma-separated values"
              />

              {/* Suggested Booking Therapy */}
              <FormControl fullWidth size="small">
                <InputLabel>Suggested Booking Therapy</InputLabel>
                <Select
                  value={filters.suggestedBookingTherapy === true ? 'true' : filters.suggestedBookingTherapy === false ? 'false' : ''}
                  onChange={(e) => onFilterChange('suggestedBookingTherapy', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)}
                  label="Suggested Booking Therapy"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Collapse>
      </Box>

      {/* Sorting Controls */}
      <Paper
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="caption" fontWeight={600}>
          Sort by:
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={sortField === 'startTime' ? (sortDirection === 'asc' ? 'Sort: Oldest First' : 'Sort: Newest First') : 'Sort by Date'}>
            <Chip
              label="Date"
              onClick={() => handleSortChange('startTime')}
              color={sortField === 'startTime' ? 'primary' : 'default'}
              variant={sortField === 'startTime' ? 'filled' : 'outlined'}
              icon={
                sortField === 'startTime' ? (
                  sortDirection === 'asc' ? (
                    <Box component="span">↑</Box>
                  ) : (
                    <Box component="span">↓</Box>
                  )
                ) : null
              }
              size="small"
            />
          </Tooltip>
        </Stack>
      </Paper>
    </Box>
  )
}

